import { SDK_ERRORS, SDK_LIMITS } from '@riz/contracts/sdk';
import { toRizError } from '../core/errors.ts';
import type { SdkClient } from '../core/client.ts';

export type AdFormat = 'rewarded' | 'interstitial';

export interface AdResult {
  /** Watched to the end. */
  completed: boolean;
  /** The server granted the reward. Never inferred from `completed`. */
  rewarded: boolean;
  skipped: boolean;
  noFill: boolean;
  watchedMs: number;
  rpEarned: number;
  error?: string;
}

export interface AdsModule {
  available(options: { placement: string; format?: AdFormat }): Promise<{ available: boolean; format: AdFormat }>;
  rewarded(options: { placement: string }): Promise<AdResult>;
  interstitial(options: { placement: string }): Promise<{ shown: boolean; noFill: boolean; error?: string }>;
  preload(options: { placement: string; format?: AdFormat }): Promise<void>;
  hasBlocker(): Promise<boolean>;
}

const NO_AD: AdResult = {
  completed: false,
  rewarded: false,
  skipped: false,
  noFill: true,
  watchedMs: 0,
  rpEarned: 0,
};

/**
 * Ads render in the Riz host document, above the game, never inside the
 * creator's frame — the frame is sandboxed without popup permission, so a
 * click-through from inside it cannot open. The game only ever sees the
 * outcome.
 *
 * Every method resolves rather than rejects for the ordinary outcomes — no
 * fill, skip, cooldown — so a game can await an ad in its normal path without
 * wrapping gameplay in a try/catch. Only a programming error rejects.
 */
export function createAdsModule(client: SdkClient): AdsModule {
  return {
    async available({ placement, format = 'rewarded' }) {
      if (!client.capabilities.has(format === 'rewarded' ? 'ads.rewarded' : 'ads.interstitial')) {
        return { available: false, format };
      }
      try {
        const result = await client.call('command', 'ads/available', { placement, format });
        return { available: result.available === true, format };
      } catch {
        return { available: false, format };
      }
    },

    async rewarded({ placement }) {
      if (!client.capabilities.has('ads.rewarded')) {
        return { ...NO_AD, error: SDK_ERRORS.capabilityDisabled };
      }

      client.setMuted(true);
      try {
        const result = await client.call(
          'command',
          'ads/request',
          { placement, format: 'rewarded' },
          SDK_LIMITS.adRequestTimeoutMs,
        );
        return {
          completed: result.completed === true,
          rewarded: result.rewarded === true,
          skipped: result.skipped === true,
          noFill: false,
          watchedMs: numberOr(result.watchedMs, 0),
          rpEarned: numberOr(result.rpEarned, 0),
        };
      } catch (raw) {
        const error = toRizError(raw);
        return {
          ...NO_AD,
          noFill: error.code === SDK_ERRORS.adNoFill || error.code === SDK_ERRORS.unfilled,
          skipped: error.code === SDK_ERRORS.adDismissed,
          error: error.code,
        };
      } finally {
        client.setMuted(false);
      }
    },

    async interstitial({ placement }) {
      if (!client.capabilities.has('ads.interstitial')) {
        return { shown: false, noFill: true, error: SDK_ERRORS.capabilityDisabled };
      }
      client.setMuted(true);
      try {
        const result = await client.call(
          'command',
          'ads/request',
          { placement, format: 'interstitial' },
          SDK_LIMITS.adRequestTimeoutMs,
        );
        return { shown: result.completed === true || result.shown === true, noFill: false };
      } catch (raw) {
        const error = toRizError(raw);
        return { shown: false, noFill: true, error: error.code };
      } finally {
        client.setMuted(false);
      }
    },

    async preload({ placement, format = 'rewarded' }) {
      if (!client.capabilities.has(format === 'rewarded' ? 'ads.rewarded' : 'ads.interstitial')) return;
      await client.call('command', 'ads/preload', { placement, format }).catch(() => {});
    },

    async hasBlocker() {
      try {
        const result = await client.call('command', 'ads/blocker', {});
        return result.hasBlocker === true;
      } catch {
        // A failed probe is not proof of a blocker; assume none and let the
        // game stay playable, which the ad requirements demand anyway.
        return false;
      }
    },
  };
}

function numberOr(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}
